import React from 'react';
import ReactDOM from 'react-dom';

/**
 * @param {Function} vjsComponent
 * @param {string} name
 * @param {React.Component} ReactComponent
 * @param {Record<string, unknown>} reactProps
 */
const wrapReactComponent = (vjsComponent, name, ReactComponent = null, reactProps = null) => {
  // vjsComponent is an old style JS "class"... e.g. `function Component() {}`
  // so to wrap, we have to extend it directly to avoid a "ComponentClass is not
  // a constructor" error

  /**
   * @param {import('video.js').VideoJsPlayer} player
   */
  function Wrapper(player) {
    Object.setPrototypeOf(this, vjsComponent.prototype);
    vjsComponent.apply(this, [player]);

    this.mount = (props) => {
      if (ReactComponent) {
        ReactDOM.render(<ReactComponent {...props} />, this.element);
      }
    };

    /* this.el() creates a div element from the extended vjsComponent class */
    this.element = this.el();
    this.element.classList.add(`vjs-${name}`);
    this.element.classList.add('hide');

    /* When player is ready, call method to mount React component */
    player.ready(() => {
      this.mount(reactProps);
    });

    /* Remove React root when component is destroyed */
    this.on('dispose', () => {
      ReactDOM.unmountComponentAtNode(this.element);
    });

    /* Allow player to update */
    player.on(`remove-${name}-class`, (event, className) => {
      this.element.classList.remove(`${className}`);
    });

    player.on(`add-${name}-class`, (event, className) => {
      this.element.classList.add(`${className}`);
    });

    player.on(`update-${name}`, (event, newProps) => {
      this.mount(newProps);
    });
  }

  return Wrapper;
};

/* 'ReactComponent' must be uppercase to render component */

/**
 * @param {import('video.js').default} videojs
 * @param {string} name
 * @param {React.Component} ReactComponent
 * @param {Record<string, unknown>} props
 */
const createVjsComponent = (videojs, name, ReactComponent, props) => {
  const vjsComponent = videojs.getComponent('Component');

  /**
   *  Creates a vjs extended function to assist with inheritance, also works with
   * 'class Name extends vjsComponent'
   */
  const v = videojs.extend(
    vjsComponent,
    wrapReactComponent(vjsComponent, name, ReactComponent, props),
  );
  return v;
};

/**
 * Registering a videojs Component requires passing the videojs
 * instance used to create the player, as each component is bound to
 * that player's ready call.
 *
 * @param {import('video.js').default} videojs
 * @param {string} name
 * @param {React.Component} ReactComponent
 * @param {Record<string, unknown>} reactProps
 */
const registerVjsComponent = (videojs, name, ReactComponent, reactProps) => {
  const component = createVjsComponent(videojs, name, ReactComponent, reactProps);
  videojs.registerComponent(name, component);
  return name;
};

export { registerVjsComponent, wrapReactComponent, createVjsComponent };
